/**
 * Authentication Context
 * Observability: Task 4.6 - Integrated with PostHog analytics
 *
 * Provides authentication state and methods throughout the app.
 */

import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import {
  createAccountWithEmail,
  getIdToken,
  isFirebaseConfigured,
  resetPassword as firebaseResetPassword,
  signInWithEmail,
  signInWithGoogle,
  signOut as firebaseSignOut,
  type User,
} from "@/features/auth/services/firebase";
import { identifyUser } from "@/services/posthog";
import { clearCsrfToken } from "@/services/api/csrf";
import { trackLogin, trackLogout } from "@/services/analytics";
import { useAuthState } from "@/features/auth/hooks/auth/useAuthState";
import { useProfileRefresh } from "@/features/auth/hooks/auth/useProfileRefresh";
import {
  debugLog,
  fetchProfileForUser,
  getApiBase,
  getCsrfHeaders,
} from "@/features/auth/hooks/auth/authSessionHelpers";
import { AuthContext, type AuthContextType, type UserProfile } from "./contexts/auth/context";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [accountDisabled, setAccountDisabled] = useState(false);
  const isConfigured = isFirebaseConfigured();

  // Name typed at registration. Sign-up fires onAuthStateChanged before
  // Firebase's updateProfile round-trip completes, so the auto-register's
  // firebaseUser.displayName can still be null; hand the entered name to
  // the registration explicitly until the profile row exists.
  const pendingSignupNameRef = useRef<string | null>(null);

  // Self-reported signup source (GTM plan F2). Same lifecycle as the name:
  // read once by the auto-register that the sign-in event triggers, then
  // dropped so a later login cannot re-attribute the account.
  const pendingSignupSourceRef = useRef<string | null>(null);

  // Generation guard: increments on sign-in/out to invalidate in-flight
  // profile refreshes that belong to a previous auth session.
  const authGenerationRef = useRef(0);

  const fetchProfile = useCallback(async (firebaseUser: User) => {
    const profile = await fetchProfileForUser(
      firebaseUser,
      setAccountDisabled,
      pendingSignupNameRef.current ?? undefined,
      pendingSignupSourceRef.current ?? undefined,
    );
    // The name has been registered (or the profile already exists); stop
    // offering it to later fetches so a renamed account is not
    // re-registered under the old name.
    if (profile !== null) {
      pendingSignupNameRef.current = null;
      pendingSignupSourceRef.current = null;
    }
    return profile;
  }, []);

  useAuthState({
    isConfigured,
    fetchProfile,
    setUser,
    setProfile,
    setLoading,
    authGenerationRef,
  });

  useProfileRefresh({
    user,
    accountDisabled,
    fetchProfile,
    setProfile,
    authGenerationRef,
  });

  const signIn = useCallback(async (email: string, password: string) => {
    debugLog("signIn called", { email });
    await signInWithEmail(email, password);
    debugLog("signIn successful");
    await trackLogin();
  }, []);

  const handleSignInWithGoogle = useCallback(async () => {
    debugLog("signInWithGoogle called");
    await signInWithGoogle();
    debugLog("signInWithGoogle successful");
    await trackLogin();
  }, []);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      name?: string,
      signupSource?: string,
    ) => {
      debugLog("signUp called", { email, hasName: !!name });
      setLoading(true);
      // Remember the entered name so the auto-register (triggered by the
      // sign-in event that fires while updateProfile is still pending) can
      // send it even if firebaseUser.displayName has not caught up.
      if (name) {
        pendingSignupNameRef.current = name;
      }
      if (signupSource) {
        pendingSignupSourceRef.current = signupSource;
      }
      try {
        await createAccountWithEmail(email, password, name);
        debugLog("signUp successful");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const handleSignOut = useCallback(async () => {
    debugLog("signOut called");
    setLoading(true);
    try {
      const apiBase = getApiBase();
      await fetch(`${apiBase}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: getCsrfHeaders(),
      }).catch(() => {
        // Ignore backend logout failures and continue with Firebase sign-out.
      });

      // pirx-i9s6p: drop the retained session token so a copied logged-out
      // cookie cannot be replayed through the header path on a later
      // session of the same browser tab.
      clearCsrfToken();
      await firebaseSignOut();
      setProfile(null);
      setAccountDisabled(false);
      debugLog("signOut successful");
      await trackLogout();
      await identifyUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    await firebaseResetPassword(email);
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    return getIdToken();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const currentGen = authGenerationRef.current;
    const userProfile = await fetchProfile(user);
    // Reject result if auth generation changed (sign-out or account switch).
    if (authGenerationRef.current !== currentGen) return;
    setProfile(userProfile);
  }, [user, fetchProfile]);

  const authValue: AuthContextType = useMemo(() => ({
    user,
    loading,
    isConfigured,
    accountDisabled,
    signIn,
    signInWithGoogle: handleSignInWithGoogle,
    signUp,
    signOut: handleSignOut,
    resetPassword,
    getToken,
    profile,
    refreshProfile,
  }), [
    user,
    loading,
    isConfigured,
    accountDisabled,
    signIn,
    handleSignInWithGoogle,
    signUp,
    handleSignOut,
    resetPassword,
    getToken,
    profile,
    refreshProfile,
  ]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}
